import { useState, useCallback } from "react";
import type { DialogProps } from "./Dialog";

export type ControlledDialogType = {
  props: DialogProps;
  setOpen: (open: boolean) => void;
  openDialog: () => void;
  closeDialog: () => void;
};

/**
 * Custom hook that manages the open state of a controlled dialog with optional callbacks.
 *
 * @param {Object} options - Configuration options for the dialog.
 * @param {Function} [options.onCloseCallback] - Optional callback triggered when the dialog is closed.
 * @param {Function} [options.onOpenCallback] - Optional callback triggered when the dialog is opened.
 * @returns {ControlledDialogType} Object containing the `open` state, handlers to control the dialog, and a setter for the `open` state.
 */
export const useControlledDialog = ({
  onCloseCallback,
  onOpenCallback,
}: {
  onCloseCallback?: () => void;
  onOpenCallback?: () => void;
} = {}): ControlledDialogType => {
  const [open, setOpen] = useState(false);

  /**
   * Handles opening the dialog and triggers the optional onOpenCallback.
   */
  const handleOpen = useCallback(() => {
    setOpen(true);
    onOpenCallback?.();
  }, [onOpenCallback]);

  /**
   * Handles closing the dialog and triggers the optional onCloseCallback.
   */
  const handleClose = useCallback(() => {
    setOpen(false);
    onCloseCallback?.();
  }, [onCloseCallback]);

  return {
    /**
     * Props to control the dialog's open state and behavior.
     */
    props: { open, onOpen: handleOpen, onClose: handleClose },

    /**
     * Manually sets the open state of the dialog.
     * @param {boolean} open - The new state of the dialog.
     */
    setOpen,
    openDialog: () => {
      setOpen(true);
    },
    closeDialog: () => {
      setOpen(false);
    },
  };
};
